/**
 * 课程表方案类型定义
 *
 * 包含领域：
 * - 课程表时段配置（Period, BellSchedulePeriods）
 * - 课程表方案（BellSchedule, BellScheduleConfig）
 * - 学期信息（Semester）
 * - 表单数据和验证（BellScheduleFormData, ValidationError）
 * - 工具函数和常量（WEEKDAY_NAMES, DEFAULT_CONFIG, 时间计算函数等）
 */

/**
 * 时段类型（与《作息时间模块底层逻辑设计》一致）：
 * - **上课（父级均为「上课」）**：`course` 普通正课；`study` 早自习 / 晚自习（由所在列表区分具体子标签）；
 * - **休息（父级均为「休息」）**：`break` 普通课间；`morning-exercise` 大课间；`lunch` 午休。
 */
export type PeriodType = 'course' | 'break' | 'morning-exercise' | 'study' | 'lunch'

export interface Period {
  id: string
  name: string
  type: PeriodType
  startTime: string
  endTime: string
  duration: number
}

export interface BellScheduleConfig {
  weekdays: number[]
  /** 网格单元格选中状态，键格式：`${rowId}-${weekday}` */
  activeCellKeys?: string[]
  totalPeriods: number
  morningPeriods: number
  afternoonPeriods: number
  morningStudy: number
  eveningStudy: number
  /** 第一节「正课」开始时间；若 `morningStudy > 0`，保存时应与 `periods.morning[0].startTime` 一致，即「末段早读结束 + breakDuration」 */
  classStartTime: string
  morningStudyDuration: number
  eveningStudyDuration: number
  classDuration: number
  breakDuration: number
  morningLongBreakDuration: number
  afternoonLongBreakDuration: number
  lunchBreakDuration: number
}

/**
 * 全日作息时段集合。业务约束（由 `validateBellScheduleTimeline` / 后端校验器强制执行）：
 * - **时间轴无空洞**：合并、按开始时间排序后，前一段结束时间须与下一段开始时间无缝衔接；
 * - **上课—休息交替**：「上课」仅含 `course`（正课）与 `study`（早/晚自习）；「休息」为 `break`（普通课间）、`morning-exercise`（大课间）、`lunch`（午休）；
 *   排序后须为「上课 → 休息 → 上课 → … → 上课」（奇数段，首尾均为上课），从而任意相邻两节「上课」之间恰好有一段「休息」。
 */
export interface BellSchedulePeriods {
  morningStudy: Period[]
  morningShortBreaks: Period[]
  /** 末段早自习结束至第一节上午正课开始之间的空档（与 morningShortBreaks 内「相邻早自习之间」的课间不同） */
  morningStudyToClassBreak: Period | null
  morning: Period[]
  morningBreak: Period | null
  morningBreakPosition: number
  lunchBreak: Period | null
  afternoon: Period[]
  afternoonShortBreaks: Period[]
  afternoonBreak: Period | null
  afternoonBreakPosition: number
  /** 末段下午正课结束至第一节晚自习开始之间的空档 */
  afternoonToEveningBreak: Period | null
  eveningStudy: Period[]
  eveningStudyShortBreaks: Period[]
}

export interface BellSchedule {
  id: number
  name: string
  createdAt: string
  updatedAt: string
  semesterId: number | null
  semesterName?: string
  config: BellScheduleConfig
  periods: BellSchedulePeriods
  /** 无已发布/已启用排课任务引用时可编辑结构 */
  structureEditable: boolean
  /** 结构锁定原因；可编辑时为 null */
  structureLockReason: string | null
}

export interface Semester {
  id: number
  name: string
  academicYearId: number
  academicYearName?: string
  typeId?: number | null
  typeName?: string | null
  startDate: string
  endDate: string
  isCurrent: boolean
}

export interface BellScheduleFormData {
  name: string
  config: BellScheduleConfig
  periods: BellSchedulePeriods
}

export interface ValidationError {
  type: 'overlap' | 'invalid' | 'gap' | 'pattern'
  message: string
  periodId: string
}

export const WEEKDAY_NAMES: Record<number, string> = {
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '日'
}

export const DEFAULT_CONFIG: BellScheduleConfig = {
  weekdays: [1, 2, 3, 4, 5],
  totalPeriods: 8,
  morningPeriods: 4,
  afternoonPeriods: 4,
  morningStudy: 1,
  eveningStudy: 2,
  classStartTime: '08:00',
  morningStudyDuration: 30,
  eveningStudyDuration: 45,
  classDuration: 45,
  breakDuration: 10,
  morningLongBreakDuration: 25,
  afternoonLongBreakDuration: 25,
  lunchBreakDuration: 120
}

/**
 * 将 `HH:mm` 或 `HH:mm:ss` 解析为从当日 0 点起的分钟数（可含小数秒，用于比较与排序）。
 */
export function parseTimeToMinutes(time: string): number {
  const t = time.trim()
  if (!t) {
    return 0
  }
  const parts = t.split(':').map(part => Number(part))
  const hours = Number.isFinite(parts[0]) ? (parts[0] ?? 0) : 0
  const minutes = Number.isFinite(parts[1]) ? (parts[1] ?? 0) : 0
  const seconds = parts.length >= 3 && Number.isFinite(parts[2]) ? (parts[2] ?? 0) : 0
  return hours * 60 + minutes + seconds / 60
}

function formatMinutesToHHmm(totalMinutes: number): string {
  const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60)
  const whole = Math.floor(normalized + 1e-9)
  const endHours = Math.floor(whole / 60) % 24
  const endMinutes = whole % 60
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
}

export function createDefaultPeriod(id: string, name: string, type: PeriodType, startTime: string, duration: number): Period {
  const endTime = calculateEndTime(startTime, duration)
  return {
    id,
    name,
    type,
    startTime,
    endTime,
    duration
  }
}

export function calculateEndTime(startTime: string, duration: number): string {
  return formatMinutesToHHmm(parseTimeToMinutes(startTime) + duration)
}

const TIME_CMP_EPS = 1e-4

/**
 * 第一节晚自习开始时间。
 * 下午时间轴末端若已晚于「最后一节下午正课结束」，说明末段正课与晚自习之间已排入唯一一段休息（普通课间或下午大课间），应直接衔接；
 * 否则末段正课后尚未插入休息，补一段 `breakDuration` 作为唯一休息后再上晚自习（与「课—休—课」规则一致）。
 */
export function resolveFirstEveningStudyStartTime(
  timelineTailAfterAfternoon: string,
  lastAfternoonCourseEndTime: string,
  breakDurationMinutes: number
): string {
  const tail = parseTimeToMinutes(timelineTailAfterAfternoon)
  const lastCourse = parseTimeToMinutes(lastAfternoonCourseEndTime)
  if (tail > lastCourse + TIME_CMP_EPS) {
    return timelineTailAfterAfternoon
  }
  return calculateEndTime(lastAfternoonCourseEndTime, breakDurationMinutes)
}

export function generatePeriodId(): string {
  return `period-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function formatWeekdays(weekdays: number[]): string {
  if (weekdays.length === 0) return ''
  if (weekdays.length === 7) return '每天'
  const sorted = [...weekdays].sort((a, b) => a - b)
  return `周${sorted.map(d => WEEKDAY_NAMES[d]).join('、')}`
}
